import { ArrowRightIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ITEM_META, ITEM_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { WorkflowStep } from '@/services/data/CampaignService';

export type WorkflowStepRenderState =
  | 'completed'
  | 'awaiting_approval'
  | 'running'
  | 'up_next'
  | 'failed';

interface WorkflowStepRowProps {
  step: WorkflowStep;
  index: number;
  state: WorkflowStepRenderState;
  /** Optional ISO timestamp shown on the right edge of the row when completed. */
  completedAt?: string | null;
  /** Active row only: rendered in the action slot; pass null for none. */
  primaryAction?: { label: string; onClick: () => void } | null;
  secondaryAction?: { label: string; onClick: () => void } | null;
  /** Body slot rendered below the row (artifact preview, failure banner, etc.). */
  children?: React.ReactNode;
}

/**
 * Status disc — communicates state via SHAPE not color (a11y per §21.2 F20):
 *  - completed         → filled disc + CheckIcon
 *  - awaiting_approval → ringed disc with `↑` glyph in center
 *  - running           → small Spinner
 *  - up_next           → empty disc with index numeral
 *  - failed            → filled disc + XIcon, text-destructive
 *
 * Pulse animation on awaiting_approval is `motion-safe:` so reduced-motion
 * users see a steady ring.
 */
function StatusDisc({
  index,
  state,
}: {
  index: number;
  state: WorkflowStepRenderState;
}): JSX.Element {
  if (state === 'completed') {
    return (
      <div
        aria-label={`Step ${index + 1} complete`}
        className="size-5 rounded-full bg-foreground/85 flex items-center justify-center shrink-0"
      >
        <CheckIcon className="size-3 text-background" weight="bold" />
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div
        aria-label={`Step ${index + 1} failed`}
        className="size-5 rounded-full bg-destructive/15 text-destructive flex items-center justify-center shrink-0"
      >
        <XIcon className="size-3" weight="bold" />
      </div>
    );
  }
  if (state === 'running') {
    return (
      <div
        aria-label={`Step ${index + 1} running`}
        className="size-5 flex items-center justify-center shrink-0"
      >
        <Spinner size="sm" />
      </div>
    );
  }
  if (state === 'awaiting_approval') {
    // Calmer awaiting-approval disc. The previous version layered a
    // weight="bold" ArrowUp glyph inside a border-2 disc — too heavy
    // visually, the chevron read as "massive" against the lightweight
    // editorial rhythm of the other rows. Subtraction discipline:
    // a thin-stroked disc with a small filled center dot. The pulse
    // animation already tells the user "this needs you" — the icon
    // should be a quiet anchor, not a competing signal.
    return (
      <div
        aria-label={`Step ${index + 1} awaiting your review`}
        className={cn(
          'size-5 rounded-full border border-foreground/70 flex items-center justify-center shrink-0',
          'motion-safe:animate-pulse',
        )}
      >
        <span aria-hidden className="size-1.5 rounded-full bg-foreground/80" />
      </div>
    );
  }
  // up_next — plain "N." numeral, matching the editorial rhythm everywhere
  // else. Status shape coding only kicks in when the step actually has a
  // state (completed/failed/running/awaiting_approval).
  return (
    <span
      aria-label={`Step ${index + 1} up next`}
      className="size-5 flex items-center justify-center shrink-0 text-detail text-muted-foreground/55"
    >
      {index + 1}.
    </span>
  );
}

function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return null;
  }
}

/**
 * One row in the workflow tracker. Composed from existing primitives only
 * (status disc, Button, Spinner, typography tokens) — no new primitive.
 *
 * Action buttons render only on the active row when state is
 * `awaiting_approval` or `failed`. Per §9.2: `flat` icon + label.
 */
export function WorkflowStepRow({
  step,
  index,
  state,
  completedAt,
  primaryAction,
  secondaryAction,
  children,
}: WorkflowStepRowProps): JSX.Element {
  const isActive = state === 'awaiting_approval' || state === 'running' || state === 'failed';
  const time = state === 'completed' ? formatTimestamp(completedAt) : null;
  const labelClass = (() => {
    switch (state) {
      case 'completed':
        return 'text-foreground/60';
      case 'failed':
        return 'text-destructive';
      case 'up_next':
        return 'text-foreground/45';
      default:
        return 'text-foreground/90';
    }
  })();
  const label = state === 'running' && step.active_form ? step.active_form : step.action;

  return (
    <li
      role="listitem"
      className={cn(
        'relative flex flex-col gap-3 px-5 py-3 transition-colors',
        'before:absolute before:left-0 before:top-3 before:bottom-3 before:w-[2px] before:rounded-full before:transition-colors',
        isActive ? 'bg-muted/30 before:bg-foreground/80' : 'before:bg-transparent',
      )}
    >
      {/* Jobs/Rams alignment fix — items-start was top-anchoring the
          status disc against a single-line label, leaving "Researching
          trending angles" floating above the spinner. Vertically centered
          now so disc + label + timestamp sit on the same baseline (the
          uses/makes meta line below stays in the body block; only the
          primary row needs centering). */}
      <div className="flex items-center gap-3">
        <StatusDisc index={index} state={state} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(ITEM_TITLE, labelClass)}>{label}</span>
            {time && (
              <span className="ml-auto shrink-0 text-detail tabular-nums text-muted-foreground/70">
                {time}
              </span>
            )}
          </div>
          {(step.uses || step.creates) && (
            <p className={cn(ITEM_META, 'mt-0.5')}>
              {step.uses && (
                <>
                  <span className="text-muted-foreground/60">Takes</span> {step.uses}
                </>
              )}
              {step.uses && step.creates && (
                <span className="px-1.5 text-muted-foreground/40">·</span>
              )}
              {step.creates && (
                <>
                  <span className="text-muted-foreground/60">Makes</span> {step.creates}
                </>
              )}
            </p>
          )}
        </div>
      </div>

      {children && <div className="pl-8">{children}</div>}

      {(primaryAction || secondaryAction) && (
        <div className="flex gap-2 justify-end">
          {secondaryAction && (
            <Button variant="flat" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button variant="flat" size="sm" onClick={primaryAction.onClick}>
              {primaryAction.label}
              <ArrowRightIcon className="size-3.5 ml-1" weight="regular" />
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
